import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../hooks/useStore';
import { setToken } from '../store/slices/auth';
import type { ApiError, AuthUserLoginResponse } from '../services/auth.service';
import { authService } from '../services/auth.service';
import { hasAllOAuthParams, extractOAuthParams } from '../utils/has-all-oauth-params';
import { Button } from '../components/ui/button';

export default function OAuthCallback() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const hasCalledRef = useRef(false);

  useEffect(() => {
    if (hasCalledRef.current) return;
    hasCalledRef.current = true;

    if (!hasAllOAuthParams(searchParams)) {
      navigate('/login', { replace: true });
      return;
    }

    const params = extractOAuthParams(searchParams);

    const doLogin = async () => {
      try {
        const [err, response]: [ApiError | null, AuthUserLoginResponse | null] =
          await authService.login(params);

        if (err || !response) {
          setError(err?.message ?? 'Authentication error. Please try again.');
          return;
        }

        const token = response?.body?.token;

        if (!token) {
          setError('Token not found in authentication response.');
          return;
        }

        dispatch(setToken(token));
        navigate('/catalog', { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unexpected authentication error.');
      }
    };

    doLogin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-4">
        <div className="text-center space-y-4">
          <p className="text-red-500 font-medium">{error}</p>
          <Button
            variant="outline"
            onClick={() => navigate('/login', { replace: true })}
          >
            Voltar ao login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="text-center space-y-3">
        <div className="w-8 h-8 border-4 border-[#00646B] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-[#3B494A] text-sm">Autenticando...</p>
      </div>
    </div>
  );
}
